import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { Toast, ToastContextValue } from '@shared/types/toast.types';
import ToastContainer from '@shared/components/ToastContainer';

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: React.ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    const newToast: Toast = {
      ...toast,
      id,
      duration: toast.duration ?? 5000, // Default 5 seconds
    };

    setToasts((prev) => [...prev, newToast]);

    // Auto-remove toast after duration (unless it's a rate-limit toast with countdown)
    if (newToast.duration && newToast.type !== 'rate-limit') {
      setTimeout(() => {
        removeToast(id);
      }, newToast.duration);
    }
  }, [removeToast]);

  const showRateLimitToast = useCallback((retryAfter: number, message?: string) => {
    const defaultMessage = `Too many requests. Please wait ${retryAfter} seconds before trying again.`;
    showToast({
      type: 'rate-limit',
      message: message || defaultMessage,
      retryAfter,
      duration: retryAfter * 1000, // Convert to milliseconds
    });
  }, [showToast]);

  // Listen for rate limit events from HTTP interceptor
  useEffect(() => {
    const handleRateLimit = (event: Event) => {
      const customEvent = event as CustomEvent<{ retryAfter: number; message?: string }>;
      showRateLimitToast(customEvent.detail.retryAfter, customEvent.detail.message);
    };

    window.addEventListener('http:rate-limit', handleRateLimit);
    return () => window.removeEventListener('http:rate-limit', handleRateLimit);
  }, [showRateLimitToast]);

  const value: ToastContextValue = {
    toasts,
    showToast,
    removeToast,
    showRateLimitToast,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};
